// Splitsen — waar gaat de andere helft heen?
//
// ── WHY A DIALOG AND NOT A DEFAULT ──────────────────────────────────────────────
// Splitting used to keep half and drop the rest on "the next working day", because there was nowhere
// to put a choice. That is a default pretending to be a decision: the half being split off is usually
// going somewhere specific, often to somebody else, and guessing means the planner has to drag it
// again immediately. Four fields is fewer actions than a guess plus a correction.
//
// ── THE ARITHMETIC IS SHOWN, NOT ENFORCED SILENTLY ──────────────────────────────
// Both halves are printed as the slider moves, so the planner never has to work out what is left.
// The server conserves the total by construction; this only makes it visible.
import { useMemo, useState } from 'react';
import * as A from '../api/planning.ts';
import { addDays, workdaysBetween, type Ymd } from '../lib/dates.ts';
import { fmt } from '../lib/capacity.ts';
import { Banner } from './bits.tsx';

export interface SplitTarget {
  projectId: number; assignmentId: number; workpackageId: number;
  day: Ymd; minutes: number; title: string; resourceId: number;
}

export function SplitDialog({ target, people, onClose, onDone }: {
  target: SplitTarget;
  people: A.Person[];
  onClose: () => void;
  onDone: (r: { keepMinutes: number; splitMinutes: number; toDay: Ymd; toResourceId: number | null }) => Promise<void>;
}) {
  // Half, rounded to a quarter hour — the unit a planner actually types.
  const [keep, setKeep] = useState(() => Math.max(15, Math.round(target.minutes / 2 / 15) * 15));
  const [toDay, setToDay] = useState<Ymd>(() => {
    let d = addDays(target.day, 1);
    while (workdaysBetween(d, d) === 0) d = addDays(d, 1);
    return d;
  });
  const [toResource, setToResource] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const split = target.minutes - keep;
  const assignable = useMemo(() => people.filter((p) => p.resourceId !== null), [people]);
  // BOTH HALVES MUST BE POSITIVE. The server refuses anything else; saying so here means the planner
  // finds out while dragging the slider rather than after pressing Splitsen.
  const legal = keep > 0 && split > 0;

  const go = async () => {
    if (!legal || busy) return;
    setBusy(true); setError(null);
    try {
      await onDone({
        keepMinutes: keep, splitMinutes: split, toDay,
        toResourceId: toResource === '' ? null : toResource,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden />
      <div className="why split-dialog" role="dialog" aria-label="Allocatie opsplitsen">
        <header className="row">
          <strong>Opsplitsen</strong>
          <span className="faint">{target.title} · {target.day}</span>
          <span className="spacer" />
          <button className="btn small" onClick={onClose} aria-label="Sluiten">✕</button>
        </header>

        {error && <Banner kind="err">{error}</Banner>}

        <p className="split-sum">
          <strong>{fmt(keep)} u</strong> blijft op {target.day}
          {' · '}
          <strong>{fmt(split)} u</strong> gaat naar {toDay}
          {toResource !== '' && ` voor ${assignable.find((p) => p.resourceId === toResource)?.displayName}`}
        </p>

        <div className="field">
          <label htmlFor="split-keep">Uren die blijven staan</label>
          <input id="split-keep" type="range" min={15} max={Math.max(15, target.minutes - 15)} step={15}
                 value={keep} onChange={(e) => setKeep(Number(e.target.value))} />
          <input type="number" min={0.25} max={target.minutes / 60} step={0.25} value={keep / 60}
                 aria-label="Uren die blijven staan"
                 onChange={(e) => setKeep(Math.round(Number(e.target.value) * 60))} />
        </div>

        <div className="field">
          <label htmlFor="split-day">Doeldatum</label>
          {/* ANY DAY, including one that is not adjacent. That is the whole point. */}
          <input id="split-day" type="date" value={toDay} onChange={(e) => setToDay(e.target.value as Ymd)} />
        </div>

        <div className="field">
          <label htmlFor="split-who">Doelmedewerker</label>
          <select id="split-who" value={toResource}
                  onChange={(e) => setToResource(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— dezelfde medewerker —</option>
            {assignable.filter((p) => p.resourceId !== target.resourceId).map((p) => (
              <option key={p.actorId} value={p.resourceId!}>{p.displayName}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="split-note">Opmerking (optioneel)</label>
          <input id="split-note" value={note} placeholder="bv. materiaal pas donderdag ter plaatse"
                 onChange={(e) => setNote(e.target.value)} />
        </div>

        <div className="row">
          <button className="btn" onClick={onClose}>Annuleren</button>
          <span className="spacer" />
          <button className="btn primary" disabled={!legal || busy} onClick={() => void go()}>
            {busy ? 'Bezig…' : 'Opsplitsen'}
          </button>
        </div>
      </div>
    </>
  );
}
